import React, { useEffect, useMemo, useState } from 'react';
import { usePastMeetings } from '../../../meetings/past-meetings/past-meetings.hook';
import { DateLabel } from '../components/DateLabel';
import { isSameDay, startOfTomorrow } from 'date-fns';
import { MeetingCard } from '../components/MeetingCard';
import { Badge, Button, Checkbox, Dropdown, Flex, Spin, Tooltip } from 'antd';
import { OptionalTooltipWrapper } from '../../../../componentsV2/OptionalTooltipWrapper';
import { useToast } from '../../../../hooks/useToast';
import { coreAPI } from '../../../../API/core';
import { DropdownVisibility } from '../../../../componentsV2/DropdownVisibility';
import { AccountModal } from '../../../meetings/account-selection/components/AccountModal';
import { DeleteOutlined, MoreOutlined, StarOutlined } from '@ant-design/icons';
import { MeetingTypeSelector } from '../UpcomingsTab/components/MeetingTypeSelector';
import PastMeetingsFilterBar from '../../components/PastMeetingsFilterBar';
import InfiniteScroll from 'react-infinite-scroll-component';
import RecapStatus from './components/RecapStatus';
import { useLibraries } from '../../../libraries/hooks/useLibraries';

import Styles from './styles';
import { NoMeetingMessage } from '../../components/NoMeetingMessage';
import { useEmail } from '../../../email/useEmail';
import { AiContextAlert } from '../components/AiContextAlert';
const { Container, CenteredContent } = Styles;

export const RecapsTab: React.FC = () => {
  const {
    hasNextPage,
    events,
    isFetching,
    canLoadData,
    selectedMeetingsTo,
    hasNoFiltersApplied,
    keepFetching,
    deleteEvent,
    updateMeetingAccount,
    updateAccountsFiltered,
    onInsightChanged,
    selectMeetingToRemove,
  } = usePastMeetings();
  const { manageInsight } = useLibraries();
  const { getEmailLink } = useEmail();
  const { success, error } = useToast();

  const [height, setHeight] = useState(800);
  const [loading, setLoading] = useState('');
  // Not using setCurrentDate to avoid unnecesary re-renders. It's only to calculate dates.
  let [currentDate] = useState(startOfTomorrow());

  const onDeleteRecap = (id: string) => {
    setLoading(id);
    const coreClient = new coreAPI();
    coreClient
      .removeRecap(id)
      .then(() => {
        deleteEvent(id);
        success('Recap removed successfully');
      })
      .catch((errorMessage: { message: string }) => {
        error(`Error removing recap: ${errorMessage.message}.`);
      })
      .finally(() => {
        setLoading('false');
      });
  };

  useEffect(() => {
    setHeight(window.outerHeight - 210);
  }, [window.screen.height, window.screen.width]);

  const meetingsBadgeCount = useMemo(() => {
    const counts: Map<string, number> = new Map();

    for (const action in selectedMeetingsTo) {
      const ids = selectedMeetingsTo[action];
      ids.forEach(id => {
        counts.set(id, (counts.get(id) || 0) + 1);
      });
    }

    return counts;
  }, [selectedMeetingsTo]);

  const meetingsSelectedActions = useMemo(() => {
    const actionRenameMap: Record<string, string> = {
      remove: 'Remove',
    };

    const actionsMap: Map<string, string[]> = new Map();

    for (const action in selectedMeetingsTo) {
      const ids = selectedMeetingsTo[action];

      ids.forEach(id => {
        if (!actionsMap.has(id)) {
          actionsMap.set(id, []);
        }
        actionsMap.get(id)?.push(actionRenameMap[action] || action);
      });
    }

    return actionsMap;
  }, [selectedMeetingsTo]);

  return (
    <Container>
      <AiContextAlert />

      {canLoadData && <PastMeetingsFilterBar />}

      {isFetching && events.length === 0 ? (
        <CenteredContent>
          <Spin />
        </CenteredContent>
      ) : (
        <InfiniteScroll
          dataLength={events.length}
          next={() => keepFetching()}
          height={height}
          hasMore={hasNextPage}
          loader={
            <div
              style={{ display: 'flex', width: '100%', justifyContent: 'center', height: '40px' }}
            >
              <Spin />
            </div>
          }
          scrollThreshold={0.6}
          endMessage={
            events.length === 0 && (
              <CenteredContent>
                <NoMeetingMessage
                  title={!hasNoFiltersApplied ? 'No Meetings Found' : ''}
                  description={
                    !hasNoFiltersApplied && (
                      <>
                        <p>
                          No meetings were found for the selected filters. Try applying different
                          filters.
                        </p>
                        <p>
                          If you're still unable to find the information you're looking for, please
                          send an email to
                          <a
                            href={getEmailLink({
                              subject: 'Trouble finding my meetings',
                              to: ['support@update.ai'],
                              body: '',
                            })}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ paddingLeft: '4px' }}
                          >
                            support@update.ai
                          </a>{' '}
                          and give us the title and date of at least one meeting that should be
                          showing up.
                        </p>
                      </>
                    )
                  }
                />
              </CenteredContent>
            )
          }
        >
          {events.map(event => {
            const comps = [];
            const date = event.scheduledStartDate;
            if (!isSameDay(new Date(date), currentDate)) {
              currentDate = new Date(date);
              comps.push(<DateLabel date={date} key={'date-label' + currentDate.toString()} />);
            }

            comps.push(
              <MeetingCard
                event={event}
                isReady={event.status === 'READY'}
                secondaryActions={[
                  event.visibility ? (
                    <>
                      <DropdownVisibility
                        hasPermissions={event.permissions.canManageWorkspaceVisibility}
                        visibility={event.visibility}
                        id={event.id}
                      />
                      <MeetingTypeSelector
                        meetingType={event.type}
                        eventId={event.id}
                        hasPermissions={event.permissions.canChangeMeetingTypes}
                      />
                    </>
                  ) : (
                    <></>
                  ),
                ]}
                tertiaryActions={[
                  <RecapStatus status={event.status!} subStatus={event.subStatus || ''} />,
                  <Dropdown
                    trigger={['click']}
                    menu={{
                      items: [
                        {
                          label: (
                            <OptionalTooltipWrapper
                              value="Only Ready recaps can be added to Library"
                              display={
                                !event.permissions.canFavoriteRecap || event.status !== 'READY'
                              }
                            >
                              Add to Library
                            </OptionalTooltipWrapper>
                          ),
                          disabled: !event.permissions.canFavoriteRecap || event.status !== 'READY',
                          icon: <StarOutlined />,
                          key: 'favorite-recap',
                          onClick: () => {
                            if (event.permissions.canFavoriteRecap)
                              manageInsight(event.id, 'recap');
                          },
                        },
                        {
                          label: (
                            <OptionalTooltipWrapper
                              value="Only the Owner can remove this Recap"
                              display={!event.permissions.canDeleteRecap || !event.isOwner}
                            >
                              <Flex gap={16}>
                                <span>Remove Recap</span>

                                <Tooltip
                                  title={
                                    event.permissions.canDeleteRecap && event.isOwner
                                      ? 'Select Meeting Recap to be removed'
                                      : null
                                  }
                                >
                                  <Checkbox
                                    checked={selectedMeetingsTo.remove?.includes(event.id) ?? false}
                                    disabled={!event.permissions.canDeleteRecap || !event.isOwner}
                                    onClick={e => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      selectMeetingToRemove(event.id);
                                    }}
                                  />
                                </Tooltip>
                              </Flex>
                            </OptionalTooltipWrapper>
                          ),
                          disabled: !event.permissions.canDeleteRecap || !event.isOwner,
                          key: 'remove',
                          icon: <DeleteOutlined />,
                          danger: true,
                          onClick: () => {
                            if (event.permissions.canDeleteRecap && event.isOwner)
                              onDeleteRecap(event.id);
                          },
                        },
                      ],
                    }}
                  >
                    <Tooltip
                      title={
                        (meetingsBadgeCount.get(event.id) ?? 0) > 0 ? (
                          <>
                            <span>This meeting recap is selected to:</span>
                            <ul style={{ margin: 0, padding: 0, paddingLeft: 16 }}>
                              {meetingsSelectedActions.get(event.id)?.map(action => (
                                <li style={{ color: 'white', margin: 0 }} key={action}>
                                  {action}
                                </li>
                              ))}
                            </ul>
                          </>
                        ) : null
                      }
                    >
                      <Badge
                        count={meetingsBadgeCount.get(event.id) ?? 0}
                        size="small"
                        showZero={false}
                      >
                        <Button
                          type="default"
                          icon={<MoreOutlined />}
                          size="small"
                          loading={loading === event.id}
                          disabled={loading === event.id}
                        />
                      </Badge>
                    </Tooltip>
                  </Dropdown>,
                ]}
                onAccountRemoved={id => updateMeetingAccount(id, null)}
                onAccountClicked={updateAccountsFiltered}
                onInsightToggleChanged={v => onInsightChanged(event.id, v)}
              />,
            );
            return <>{comps}</>;
          })}
        </InfiniteScroll>
      )}
      <AccountModal onAccountChanged={updateMeetingAccount} />
    </Container>
  );
};
